import { useSyncExternalStore } from 'react';

import { ringBuffer } from './ringBuffer';
import type { Entry } from './ringBuffer';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Trailing debounce window (ms). N pushes within this window → 1 version bump. */
export const FLUSH_MS = 250;

// ─── Module-level state ───────────────────────────────────────────────────────

let _version = 0;
const _subs = new Set<() => void>();
let _timer: ReturnType<typeof setTimeout> | null = null;

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Schedules a trailing debounce flush. Each call restarts the timer so that N
 * rapid pushes within FLUSH_MS collapse into exactly 1 version bump (IAC-12).
 */
function scheduleFlush(): void {
  if (_timer !== null) clearTimeout(_timer);
  _timer = setTimeout(() => {
    _timer = null;
    _version++;
    _subs.forEach((cb) => cb());
  }, FLUSH_MS);
}

// ─── Exported store ───────────────────────────────────────────────────────────

export const store = {
  /**
   * Called by consoleCapture.pushEntry() after every ring-buffer push.
   * Schedules a debounced version bump so React flushes at most once per
   * FLUSH_MS window (IAC-12).
   */
  notify(): void {
    scheduleFlush();
  },

  /**
   * Registers a callback invoked each time the version counter increments.
   * Compatible with the `subscribe` argument of `useSyncExternalStore`.
   * Returns an unsubscribe function.
   */
  subscribe(cb: () => void): () => void {
    _subs.add(cb);
    return () => {
      _subs.delete(cb);
    };
  },

  /**
   * Returns the current version counter — a stable primitive that
   * `useSyncExternalStore` can compare by value to decide whether to
   * re-render. Does NOT change between flushes.
   */
  getVersion(): number {
    return _version;
  },

  /**
   * Returns all entries currently in the ring buffer. Called by the component
   * inside render (after `useSyncExternalStore` triggers a re-render via version
   * change). Not used as the snapshot; see `getVersion()` for that.
   */
  getEntries(): Entry[] {
    return ringBuffer.getAll();
  },

  /**
   * Clears the ring buffer and immediately bumps the version counter so
   * subscribers (and therefore the React component) see the empty state
   * without waiting for the next FLUSH_MS window.
   */
  clear(): void {
    ringBuffer.clear();
    if (_timer !== null) {
      clearTimeout(_timer);
      _timer = null;
    }
    _version++;
    _subs.forEach((cb) => cb());
  },
};

// ─── useSyncExternalStore hook ────────────────────────────────────────────────

/**
 * React hook that returns the current console entries, re-rendering whenever
 * `store.notify()` triggers a debounced version bump.
 *
 * Uses `useSyncExternalStore` with a version-number snapshot (stable primitive)
 * to avoid tearing; the component reads `getEntries()` after each version change.
 */
export function useConsoleCapture(): Entry[] {
  useSyncExternalStore(
    store.subscribe,
    () => store.getVersion(),
    () => store.getVersion(),
  );
  return store.getEntries();
}
